"use client";

/**
 * RestaurantPublicHero
 * App-like hero section shown on the public restaurant profile page.
 * Mirrors the UX polish of StayProfileHeroGallery and the Fix-it Status card.
 *
 * Shows:
 * - Today's Special banner (if set)
 * - Occasion tags (popularOccasions)
 * - WhatsApp Table Reservation button (if enabled)
 * - Compliance trust badges (COA, Fire Safety, Liquor Licence)
 * - Quick-facts strip (cuisine, price range, service modes, parking, power backup)
 */

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  UtensilsCrossed,
  Zap,
  ShieldCheck,
  ParkingCircle,
  MessageCircle,
  Phone,
  Share2,
  Star,
  Clock,
  Bike,
  ChefHat,
  CheckCircle2,
} from "lucide-react";

// ── Types ────────────────────────────────────────────────────────────────────

interface RestaurantPublicHeroProps {
  businessName: string;
  whatsappNumber?: string | null;
  /** Present when the restaurant profile is the current page — used for the share URL. */
  profileId?: string | null;
  restaurantTableWhatsappEnabled?: boolean;
  restaurantTodaySpecial?: string | null;
  /** Deep link to this restaurant's card on the Craving discovery page (menu + order). */
  cravingHref?: string | null;
  popularOccasions?: string[];
  cuisineType?: string | null;
  priceRange?: string | null;
  serviceModes?: string[];
  secureParking?: boolean;
  powerBackup?: string | null;
  averageMealPrice?: string | null;
  reservationSupport?: string | null;
  deliveryAvailability?: string | null;
  // Compliance verification statuses
  coaCertificateStatus?: string | null;
  emsFireSafetyStatus?: string | null;
  liquorLicenseStatus?: string | null;
  foodHandlerStatus?: string | null;
  cipcStatus?: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const isVerified = (v?: string | null) =>
  typeof v === "string" && ["verified", "yes"].includes(v.trim().toLowerCase());

const toWhatsAppUrl = (raw?: string | null, message?: string): string | null => {
  if (!raw) return null;
  // Normalise: strip spaces, dashes, +; ensure starts with country code
  const digits = raw.replace(/[\s\-\+\(\)]/g, "");
  const e164 = digits.startsWith("0")
    ? "27" + digits.slice(1)
    : digits.startsWith("27")
    ? digits
    : "27" + digits;
  const encoded = encodeURIComponent(message || "");
  return `https://wa.me/${e164}${encoded ? `?text=${encoded}` : ""}`;
};

const OCCASION_ICONS: Record<string, string> = {
  "Birthday dinners": "🎂",
  "Date nights": "🥂",
  "Family lunches": "👨‍👩‍👧",
  "Business lunches": "💼",
  "Private events": "🎉",
  "Casual dining": "🍽",
  "Sports viewing": "📺",
  "Sunday roasts": "🥩",
  "Breakfast / Brunch": "☕",
  "Year-end functions": "🎊",
};

const occasionIcon = (label: string) => OCCASION_ICONS[label] || "✨";

const PRICE_RANGE_LABEL: Record<string, string> = {
  R: "Budget friendly",
  RR: "Mid-range",
  RRR: "Upmarket",
  RRRR: "Fine dining",
};

// ── Component ─────────────────────────────────────────────────────────────────

export function RestaurantPublicHero({
  businessName,
  whatsappNumber,
  profileId,
  restaurantTableWhatsappEnabled,
  restaurantTodaySpecial,
  cravingHref,
  popularOccasions = [],
  cuisineType,
  priceRange,
  serviceModes = [],
  secureParking,
  powerBackup,
  averageMealPrice,
  reservationSupport,
  deliveryAvailability,
  coaCertificateStatus,
  emsFireSafetyStatus,
  liquorLicenseStatus,
  foodHandlerStatus,
  cipcStatus,
}: RestaurantPublicHeroProps) {
  const [shareCopied, setShareCopied] = useState(false);

  const hasWhatsApp =
    restaurantTableWhatsappEnabled && Boolean(whatsappNumber);

  const whatsappMessage = `Hi ${businessName}, I found you on Zamzi Connect and would like to reserve a table. Please share your availability and booking details.`;
  const whatsappUrl = hasWhatsApp
    ? toWhatsAppUrl(whatsappNumber, whatsappMessage)
    : null;

  const modesLower = serviceModes.map((m) => m.toLowerCase());
  const supportsOrderAhead = modesLower.some((m) => m.includes('delivery') || m.includes('takeaway'));
  const orderMessage = `Hi ${businessName}! Saw your spot on Zamzi Connect. I'd love to order — could you please share your menu or let me know what your specials are? Best way: reply with your menu link or WhatsApp your items + name + address (if delivery/takeaway). Thanks!`;
  const orderAheadUrl = supportsOrderAhead && whatsappNumber
    ? toWhatsAppUrl(whatsappNumber, orderMessage)
    : null;

  const shareUrl =
    typeof window !== "undefined"
      ? profileId
        ? `${window.location.origin}/profile/${profileId}`
        : window.location.href
      : "";

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${businessName} on Zamzi Connect`,
          text: `Check out ${businessName} on Zamzi Connect`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      }
    } catch {
      // User cancelled share — no action needed
    }
  };

  const hasTodaySpecial = Boolean(restaurantTodaySpecial?.trim());
  const hasOccasions = popularOccasions.length > 0;

  const complianceBadges = [
    {
      label: "Health & Safety (COA)",
      status: coaCertificateStatus,
      icon: <ShieldCheck className="h-3.5 w-3.5" />,
    },
    {
      label: "Fire Safety",
      status: emsFireSafetyStatus,
      icon: <ShieldCheck className="h-3.5 w-3.5" />,
    },
    {
      label: "Liquor Licence",
      status: liquorLicenseStatus,
      icon: <ShieldCheck className="h-3.5 w-3.5" />,
    },
    {
      label: "Food Handler",
      status: foodHandlerStatus,
      icon: <ShieldCheck className="h-3.5 w-3.5" />,
    },
    {
      label: "Business Registered",
      status: cipcStatus,
      icon: <ShieldCheck className="h-3.5 w-3.5" />,
    },
  ].filter((b) => isVerified(b.status));

  const hasFacts =
    cuisineType || priceRange || serviceModes.length > 0 || secureParking || powerBackup;

  // Nothing to show — render nothing rather than an empty card
  if (
    !hasTodaySpecial &&
    !hasOccasions &&
    !whatsappUrl &&
    !orderAheadUrl &&
    !cravingHref &&
    complianceBadges.length === 0 &&
    !hasFacts
  ) {
    return null;
  }

  return (
    <div className="mt-6 space-y-4">

      {/* ── Today's Special Banner ─────────────────────────────────────────── */}
      {hasTodaySpecial && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-4 shadow-sm">
          <span className="text-2xl leading-none mt-0.5">🍽</span>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-700">
              Today's Special
            </p>
            <p className="mt-0.5 text-base font-semibold text-amber-900">
              {restaurantTodaySpecial}
            </p>
          </div>
        </div>
      )}

      {/* ── Order & view menu on Craving — the discovery card ────────────── */}
      {cravingHref && (
        <a href={cravingHref} className="block">
          <Button
            className="w-full h-12 text-base font-semibold shadow-md"
            style={{ backgroundColor: "#001489", color: "#ffffff" }}
          >
            <UtensilsCrossed className="mr-2 h-5 w-5" />
            View menu &amp; order on Craving
          </Button>
        </a>
      )}

      {/* ── WhatsApp Reserve Button + Share / Call ───────────────────────── */}
      {(whatsappUrl || profileId) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {whatsappUrl && (
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="block">
              <Button
                className="w-full h-12 text-base font-semibold shadow-md"
                style={{ backgroundColor: "#25D366", color: "#ffffff" }}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Reserve a Table via WhatsApp
              </Button>
            </a>
          )}
          {profileId && (
            <Button
              className="w-full h-12 text-base font-semibold shadow-md"
              variant="outline"
              onClick={() => void handleShare()}
            >
              {shareCopied ? (
                <CheckCircle2 className="mr-2 h-5 w-5 text-emerald-600" />
              ) : (
                <Share2 className="mr-2 h-5 w-5" />
              )}
              {shareCopied ? "Link copied!" : "Share"}
            </Button>
          )}
        </div>
      )}
      {whatsappNumber && (
        <a
          href={`tel:${whatsappNumber.replace(/[^+\d]/g, "")}`}
          className="block"
        >
          <Button
            className="w-full h-12 text-base font-semibold shadow-md"
            style={{ backgroundColor: "#0077B6", color: "#ffffff" }}
          >
            <Phone className="mr-2 h-5 w-5" />
            Call the Restaurant
          </Button>
        </a>
      )}

      {orderAheadUrl && (
        <a href={orderAheadUrl} target="_blank" rel="noopener noreferrer" className="block">
          <Button
            className="w-full h-12 text-base font-semibold shadow-md border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
            variant="outline"
          >
            <Bike className="mr-2 h-5 w-5" />
            Order or Ask for Menu
          </Button>
        </a>
      )}

      {/* ── Occasion Tags ─────────────────────────────────────────────────── */}
      {hasOccasions && (
        <Card className="border-pink-100 bg-white shadow-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-pink-700 mb-3">
              Perfect for
            </p>
            <div className="flex flex-wrap gap-2">
              {popularOccasions.map((occasion) => (
                <span
                  key={occasion}
                  className="inline-flex items-center gap-1.5 rounded-full border border-pink-100 bg-pink-50 px-3 py-1 text-sm font-medium text-pink-800"
                >
                  <span>{occasionIcon(occasion)}</span>
                  {occasion}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Quick Facts Strip ─────────────────────────────────────────────── */}
      {hasFacts && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {cuisineType && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 shadow-sm">
              <ChefHat className="h-4 w-4 shrink-0 text-slate-500" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Cuisine</p>
                <p className="text-sm font-semibold text-slate-800 leading-tight">{cuisineType}</p>
              </div>
            </div>
          )}
          {priceRange && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 shadow-sm">
              <Star className="h-4 w-4 shrink-0 text-slate-500" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Price</p>
                <p className="text-sm font-semibold text-slate-800 leading-tight">
                  {priceRange} — {PRICE_RANGE_LABEL[priceRange] ?? priceRange}
                </p>
              </div>
            </div>
          )}
          {averageMealPrice && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 shadow-sm">
              <UtensilsCrossed className="h-4 w-4 shrink-0 text-slate-500" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Avg meal</p>
                <p className="text-sm font-semibold text-slate-800 leading-tight">{averageMealPrice}</p>
              </div>
            </div>
          )}
          {serviceModes.length > 0 && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 shadow-sm">
              <Bike className="h-4 w-4 shrink-0 text-slate-500" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Service</p>
                <p className="text-sm font-semibold text-slate-800 leading-tight">
                  {serviceModes.slice(0, 2).join(" · ")}
                </p>
              </div>
            </div>
          )}
          {reservationSupport && reservationSupport !== "None" && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 shadow-sm">
              <Clock className="h-4 w-4 shrink-0 text-slate-500" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Reservations</p>
                <p className="text-sm font-semibold text-slate-800 leading-tight">{reservationSupport}</p>
              </div>
            </div>
          )}
          {secureParking && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 shadow-sm">
              <ParkingCircle className="h-4 w-4 shrink-0 text-slate-500" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Parking</p>
                <p className="text-sm font-semibold text-slate-800 leading-tight">Secure parking</p>
              </div>
            </div>
          )}
          {powerBackup && powerBackup !== "None" && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 shadow-sm">
              <Zap className="h-4 w-4 shrink-0 text-slate-500" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Power backup</p>
                <p className="text-sm font-semibold text-slate-800 leading-tight">{powerBackup}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Compliance Trust Badges ───────────────────────────────────────── */}
      {complianceBadges.length > 0 && (
        <Card className="border-emerald-100 bg-emerald-50/60 shadow-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700 mb-3">
              Verified Compliance
            </p>
            <div className="flex flex-wrap gap-2">
              {complianceBadges.map((badge) => (
                <span
                  key={badge.label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold text-emerald-800"
                >
                  {badge.icon}
                  {badge.label}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
